import { useEffect } from 'react';
import {
  currentOrPastDateSchema,
  currentOrPastDateUI,
  currentOrPastMonthYearDateSchema,
  currentOrPastMonthYearDateUI,
} from 'platform/forms-system/src/js/web-component-patterns';

import {
  addStyleToShadowDomOnPages,
  ForceFieldBlur,
  validateApproximateDate,
} from './utils';

// Hint text for the month/year-only (all-claims-v2) date variant. Kept in one
// place so both condition date pages stay consistent.
const MONTH_YEAR_ONLY_HINT =
  'You don’t have to provide this date. Select a month and enter 4 digits for the year.';

// Hides the built-in “For example: January 19 2000”
export const HideDefaultDateHint = () => {
  useEffect(() => {
    // Inject the style into every <va-memorable-date> on the page
    addStyleToShadowDomOnPages(
      [''],
      ['va-memorable-date'],
      '#dateHint {display:none}',
    );
  }, []);

  return null; // nothing visual – side-effect only
};

/**
 * uiSchema for date field shared by the new condition and rated disability paths.
 *
 * @param {Object} options
 * @param {string} options.title - Field label
 * @param {string} options.fullDateHint - Hint text for the full
 *   month/day/year (all-claims v1) variant
 * @param {boolean} [options.monthYearOnly=false] - When true, renders only
 *   the month and year inputs, otherwise also renders the day input.
 * @param {Array} [options.validations=[]] - Extra 'ui:validations' appended
 *   after validateApproximateDate
 * @returns {UISchemaOptions}
 */
export const conditionDateUI = ({
  title,
  fullDateHint,
  monthYearOnly = false,
  validations = [],
}) => {
  const dateUI = monthYearOnly
    ? currentOrPastMonthYearDateUI({ title, hint: MONTH_YEAR_ONLY_HINT })
    : currentOrPastDateUI({ title, hint: fullDateHint });

  return {
    ...dateUI,
    // run the effect here
    'ui:description': HideDefaultDateHint,
    'ui:validations': [validateApproximateDate, ...validations],
  };
};

/**
 * Schema for the condition date field, matching conditionDateUI.
 *
 * @param {boolean} [monthYearOnly=false] - Must match the value passed to
 *   conditionDateUI
 * @returns {SchemaOptions}
 */
export const conditionDateSchema = (monthYearOnly = false) =>
  monthYearOnly ? currentOrPastMonthYearDateSchema : currentOrPastDateSchema;

// Aim is to trigger internal VA form field update logic
export const forceFieldBlurUI = {
  'ui:field': ForceFieldBlur,
};

// Required for ForceFieldBlur to render
export const forceFieldBlurSchema = { type: 'boolean' };
